import React, { useCallback, useEffect, useRef, useState } from "react";
import { Platform, ScrollView, StyleSheet, Text, TouchableOpacity, View, useWindowDimensions } from "react-native";
import { useSelector } from "react-redux";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useTheme } from "../../../../../theme/useTheme";
import { RootState, useAppDispatch } from "../../../../../store";
import {
  fetchAsnLocationDetails,
  ASNLocationDetailsRow,
  AsnLocationGrainKey,
} from "../api/asnOverviewKpiApi";
import {
  AsnLocationSummaryPerspective,
  selectAsnLocationSummaryPerspective,
  setAsnLocationSummaryPerspective,
  setAsnLocationSummaryLoading,
  setAsnLocationSummaryData,
  setAsnLocationSummaryError,
  setAsnLocationSummaryHasLoadedOnce,
  setAsnLocationSummaryLastRequest,
  setAsnLocationSummarySort,
  setAsnLocationSummarySearchQuery,
  setAsnLocationSummaryPageNumber,
  setAsnLocationSummaryPageSize,
  buildAsnLocationSummaryCacheKey,
} from "../../../../../store/slices/asnLocationSummarySlice";
import {
  buildAsnLocationDetailsRequest,
  AsnOverviewKpiRequestInputs,
} from "../utils/asnOverviewKpiRequestBuilder";
import LocationSummaryV2Table from "./LocationSummaryV2Table";
import LocationSummaryV2CompanionTable from "./LocationSummaryV2CompanionTable";
import { v2Styles as styles } from "./LocationSummaryV2.styles";
import { AsnMetricColumn } from "../constants/asnOnePagerConstants";

const MOBILE_BREAKPOINT = 640;

export interface LocationSummaryV2Props {
  filterInputs: AsnOverviewKpiRequestInputs;
  /** Invoked when the user clicks "Export to Excel" in the table toolbar; receives
   * the metric and grain columns currently visible in the table, in on-screen order. */
  onExport: (visibleMetricColumns: AsnMetricColumn[], visibleGrainKeys: AsnLocationGrainKey[]) => void;
  /** True while an export is in flight — passed through to the table toolbar. */
  isExporting?: boolean;
  /** True once the time filter dropdown has been fully pre-populated with its
   * initial/default values (e.g. the current period has been resolved from
   * the backend). The initial API call is deferred until this is true so
   * the request is never fired with an empty period filter. Defaults to true
   * so omitting this prop preserves backward-compatible behaviour. */
  isFilterReady?: boolean;
}

interface RunFetchOverrides {
  perspective?: AsnLocationSummaryPerspective;
  sortBy?: string;
  sortDirection?: "asc" | "desc";
  searchQuery?: string;
  pageNumber?: number;
  pageSize?: number;
}

const SEARCH_DEBOUNCE_MS = 350;

const LocationSummaryV2: React.FC<LocationSummaryV2Props> = ({
  filterInputs,
  onExport,
  isExporting = false,
  isFilterReady = true,
}) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const { width: windowWidth } = useWindowDimensions();
  const isMobile = windowWidth < MOBILE_BREAKPOINT;
  const dispatch = useAppDispatch();

  // ─── Redux-backed state ───────────────────────────────────────────────
  const perspective = useSelector((state: RootState) =>
    selectAsnLocationSummaryPerspective(state),
  );
  const grainSelections = useSelector(
    (state: RootState) => state.asnLocationSummary.grainSelections,
  );
  const sortBy = useSelector(
    (state: RootState) => state.asnLocationSummary.sortBy,
  );
  const sortDirection = useSelector(
    (state: RootState) => state.asnLocationSummary.sortDirection,
  );
  const searchQuery = useSelector(
    (state: RootState) => state.asnLocationSummary.searchQuery,
  );
  const pageNumber = useSelector(
    (state: RootState) => state.asnLocationSummary.pageNumber,
  );
  const pageSize = useSelector(
    (state: RootState) => state.asnLocationSummary.pageSize,
  );
  const hasLoadedOnce = useSelector(
    (state: RootState) => state.asnLocationSummary.hasLoadedOnce,
  );
  const applyVersion = useSelector(
    (state: RootState) => state.asnLocationSummary.applyVersion,
  );
  const cache = useSelector(
    (state: RootState) => state.asnLocationSummary.cache,
  );
  const data = useSelector(
    (state: RootState) => state.asnLocationSummary.data,
  );
  const loading = useSelector(
    (state: RootState) => state.asnLocationSummary.loading,
  );
  const error = useSelector(
    (state: RootState) => state.asnLocationSummary.error,
  );

  const effectiveGrainKeys: AsnLocationGrainKey[] =
    data?.effectiveGrainKeys?.length
      ? data.effectiveGrainKeys
      : grainSelections;

  // ─── Selected companion anchor row ────────────────────────────────────
  const [selectedRow, setSelectedRow] = useState<
    ASNLocationDetailsRow | undefined
  >();

  // ─── Scroll-to-companion refs ─────────────────────────────────────────
  // When a row is selected the companion table is conditionally mounted below
  // the main table. We scroll the inner ScrollView down to bring it into view.
  const outerScrollRef = useRef<ScrollView>(null);
  const companionRef   = useRef<View>(null);

  const handleRowSelect = useCallback((row: ASNLocationDetailsRow) => {
    setSelectedRow(row);
    // Scroll after a short delay so the companion View has been mounted and
    // laid out before we measure it.
    setTimeout(() => {
      if (!outerScrollRef.current || !companionRef.current) return;

      if (Platform.OS === "web") {
        // On web, use the native scrollIntoView on the underlying DOM node.
        const node = (companionRef.current as any)?._nativeTag ?? companionRef.current;
        if (typeof (node as any)?.scrollIntoView === "function") {
          (node as any).scrollIntoView({ behavior: "smooth", block: "start" });
          return;
        }
      }

      // Native: measure the companion View's y-offset relative to the
      // ScrollView's scroll container, then scrollTo that position.
      (companionRef.current as any).measureLayout(
        (outerScrollRef.current as any).getScrollableNode?.() ??
          (outerScrollRef.current as any)._scrollViewRef?.current,
        (_x: number, y: number) => {
          outerScrollRef.current?.scrollTo({ y: Math.max(0, y - 12), animated: true });
        },
        () => {
          // measureLayout failed — fall back to scrollToEnd so companion is
          // at least visible.
          outerScrollRef.current?.scrollToEnd({ animated: true });
        },
      );
    }, 100);
  }, []);

  // ─── Stale-response guard ─────────────────────────────────────────────
  // Only the response whose requestId matches requestIdRef.current is applied.
  const requestIdRef = useRef(0);

  // ─── Search input (local) with debounce ───────────────────────────────
  const [searchInput, setSearchInput] = useState(searchQuery);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    setSearchInput(searchQuery);
  }, [searchQuery]);
  useEffect(
    () => () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    },
    [],
  );

  // ─── Core fetch: checks Redux cache before hitting the API ────────────
  const runFetch = useCallback(
    (overrides?: RunFetchOverrides) => {
      const request = buildAsnLocationDetailsRequest({
        ...filterInputs,
        perspective: overrides?.perspective ?? perspective,
        grainSelections,
        sortBy: overrides?.sortBy ?? sortBy,
        sortDirection: (
          overrides?.sortDirection ?? sortDirection
        ).toUpperCase() as "ASC" | "DESC",
        searchQuery: overrides?.searchQuery ?? searchQuery,
        pageNumber: overrides?.pageNumber ?? pageNumber,
        pageSize: overrides?.pageSize ?? pageSize,
      });

      dispatch(setAsnLocationSummaryLastRequest(request));

      const requestKey = buildAsnLocationSummaryCacheKey(request);
      const cached = cache[requestKey];
      if (cached) {
        dispatch(setAsnLocationSummaryData({ response: cached, requestKey }));
        return;
      }

      const requestId = ++requestIdRef.current;
      dispatch(setAsnLocationSummaryLoading());
      fetchAsnLocationDetails(request)
        .then((response) => {
          if (requestId !== requestIdRef.current) return; // stale — discard
          dispatch(setAsnLocationSummaryData({ response, requestKey }));
        })
        .catch((err: any) => {
          if (requestId !== requestIdRef.current) return; // stale — discard
          const msg =
            err?.response?.data?.message ??
            err?.message ??
            "An unexpected error occurred while loading the ASN Gap Location Summary.";
          dispatch(setAsnLocationSummaryError(msg));
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dispatch,
      filterInputs,
      perspective,
      grainSelections,
      sortBy,
      sortDirection,
      searchQuery,
      pageNumber,
      pageSize,
      cache,
    ],
  );

  // ─── Trigger 1: initial load (first activation of this tab) ──────────
  // Deferred until isFilterReady is true so the request is never fired
  // with an empty period filter (before currentPeriodWeek has resolved).
  useEffect(() => {
    if (!isFilterReady) return;
    if (hasLoadedOnce) return;
    dispatch(setAsnLocationSummaryHasLoadedOnce());
    runFetch({ pageNumber: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFilterReady]);

  // ─── Trigger 2: shared filter bar Apply/Reset ─────────────────────────
  const isFirstApplyVersionRun = useRef(true);
  useEffect(() => {
    if (isFirstApplyVersionRun.current) {
      isFirstApplyVersionRun.current = false;
      return;
    }
    runFetch({ pageNumber: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyVersion]);

  // ─── Perspective toggle ───────────────────────────────────────────────
  const handlePerspectiveChange = useCallback(
    (next: AsnLocationSummaryPerspective) => {
      if (next === perspective) return;
      dispatch(setAsnLocationSummaryPerspective(next));
      setSelectedRow(undefined);
      runFetch({ perspective: next, pageNumber: 0 });
    },
    [dispatch, perspective, runFetch],
  );

  // ─── Sort ─────────────────────────────────────────────────────────────
  const handleSort = useCallback(
    (col: string) => {
      const nextDir: "asc" | "desc" =
        sortBy === col && sortDirection === "asc" ? "desc" : "asc";
      dispatch(
        setAsnLocationSummarySort({ sortBy: col, sortDirection: nextDir }),
      );
      runFetch({ sortBy: col, sortDirection: nextDir, pageNumber: 0 });
    },
    [dispatch, sortBy, sortDirection, runFetch],
  );

  // ─── Pagination ───────────────────────────────────────────────────────
  const handlePageChange = useCallback(
    (nextPage: number) => {
      dispatch(setAsnLocationSummaryPageNumber(nextPage));
      runFetch({ pageNumber: nextPage });
    },
    [dispatch, runFetch],
  );

  const handlePageSizeChange = useCallback(
    (nextSize: number) => {
      dispatch(setAsnLocationSummaryPageSize(nextSize));
      runFetch({ pageSize: nextSize, pageNumber: 0 });
    },
    [dispatch, runFetch],
  );

  // ─── Search (debounced 350 ms) ────────────────────────────────────────
  const handleSearchChange = useCallback(
    (text: string) => {
      setSearchInput(text);
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = setTimeout(() => {
        dispatch(setAsnLocationSummarySearchQuery(text));
        runFetch({ searchQuery: text, pageNumber: 0 });
      }, SEARCH_DEBOUNCE_MS);
    },
    [dispatch, runFetch],
  );

  const label = perspective === "shipping" ? "Shipping" : "Receiving";

  return (
    <ScrollView
      ref={outerScrollRef}
      style={styles.container}
      contentContainerStyle={{ flexGrow: 1 }}
      testID="asn-gap-location-summary-v2"
      showsVerticalScrollIndicator={false}
      keyboardShouldPersistTaps="handled"
      nestedScrollEnabled
    >
      <View style={[styles.viewBar, isMobile && mobileStyles.viewBarMobile]}>
        <Text style={styles.label}>Perspective</Text>
        {/* On mobile: column layout so both buttons get full width */}
        <View style={[styles.segmented, isMobile && mobileStyles.segmentedMobile]}>
          {(["shipping", "receiving"] as const).map((value, index) => {
            const isActive = perspective === value;
            const iconColor = isActive ? "#FFFFFF" : isDark ? "#9CA3AF" : "#075CB5";
            const textColor = isActive ? "#FFFFFF" : isDark ? "#D1D5DB" : "#495057";

            return (
              <TouchableOpacity
                key={value}
                style={[
                  styles.segment,
                  !isActive && { backgroundColor: "#EFF5FC" },
                  !isActive && isDark && {
                    backgroundColor: "#2B2B2B",
                    borderWidth: 1,
                    borderColor: "#4A4A4A",
                  },
                  isActive && styles.activeSegment,
                  isMobile && mobileStyles.segmentMobile,
                  isMobile && index === 0 && mobileStyles.firstSegmentMobile,
                ]}
                onPress={() => handlePerspectiveChange(value)}
                testID={`asn-gap-perspective-${value}`}
              >
                <Text style={{ color: iconColor }}>
                  {value === "shipping" ? (
                    <MaterialIcons
                      name="call-made"
                      size={16}
                      color={iconColor}
                    />
                  ) : (
                    <MaterialIcons
                      name="call-received"
                      size={16}
                      color={iconColor}
                    />
                  )}
                </Text>
                <Text
                  style={[
                    styles.segmentText,
                    !isActive && { color: textColor },
                    isActive && styles.activeSegmentText,
                  ]}
                >
                  View {value[0].toUpperCase() + value.slice(1)} Locations
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>
        <Text style={[styles.hint, isMobile && mobileStyles.hintMobile]}>
          {label} leads. Select a row to reveal linked{" "}
          {perspective === "shipping" ? "receiving" : "shipping"} locations.
        </Text>
        <TouchableOpacity
          style={[styles.linkButton, isMobile && mobileStyles.linkButtonMobile]}
          onPress={() =>
            handlePerspectiveChange(
              perspective === "shipping" ? "receiving" : "shipping",
            )
          }
          testID="asn-gap-flip-perspective"
        >
          <MaterialIcons name="swap-horiz" size={16} color="#075CB5" />
          <Text style={[styles.linkText, isMobile && mobileStyles.linkTextMobile]}>Flip perspective</Text>
        </TouchableOpacity>
      </View>

      <LocationSummaryV2Table
        apiRows={data?.rows ?? []}
        perspective={perspective}
        effectiveGrainKeys={effectiveGrainKeys}
        grandTotal={data?.grandTotal ?? null}
        serverPagination={data?.pagination ?? null}
        loading={loading}
        error={error}
        sortBy={sortBy}
        sortDirection={sortDirection}
        searchInput={searchInput}
        selectedKey={selectedRow?.key}
        onSelect={handleRowSelect}
        onSort={handleSort}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onSearchChange={handleSearchChange}
        onExport={onExport}
        isExporting={isExporting}
      />

      <View ref={companionRef}>
        <LocationSummaryV2CompanionTable
          perspective={perspective}
          anchorRow={selectedRow}
          effectiveGrainKeys={effectiveGrainKeys}
          filterInputs={filterInputs}
          onClear={() => setSelectedRow(undefined)}
        />
      </View>
    </ScrollView>
  );
};

// ─── Mobile-only styles (never applied on web) ────────────────────────────────

const mobileStyles = StyleSheet.create({
  // Make the viewBar stack its children vertically so buttons don't overflow
  viewBarMobile: {
    flexDirection: "column",
    alignItems: "stretch",
  },
  // Stack segment buttons vertically, each taking full width, with gap between them
  segmentedMobile: {
    flexDirection: "column",
    width: "100%",
    gap: 0,
    paddingBottom: 0,
  },
  // Each button fills the row width
  segmentMobile: {
    width: "100%",
    justifyContent: "center",
  },
  firstSegmentMobile: {
    marginBottom: 8,
  },
  // Hint text doesn't need minWidth constraint on mobile
  hintMobile: {
    minWidth: 0,
    flex: 0,
    width: "100%",
  },
  // Keep flip-perspective action centered on mobile
  linkButtonMobile: {
    width: "100%",
    justifyContent: "center",
  },
  linkTextMobile: {
    textAlign: "center",
  },
});

export default LocationSummaryV2;
